import React, { useEffect, useState } from "react";
import type { CreateClientDto, UpdateClientDto } from "../api/clients";
import { CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { authApi } from "../utils/httpClient";
import { ApiResponse } from "@industronics/fids-utils";

export type ClientFormMode = "create" | "edit";

type Plan = {
    _id: string;
    planKey: string;
    name: string;
    allowedDevicesLimit: number;
    isActive?: boolean;
    priceYearly?: number;
    currency?: string;
    billingCycle?: string;
    description?: string;
};

interface Props {
    open: boolean;
    mode: ClientFormMode;
    initial?: UpdateClientDto;
    onClose: () => void;
    onSubmit: (payload: CreateClientDto) => Promise<void> | void;
    submitting?: boolean;
    error?: string | null;
    fieldErrors?: Partial<Record<keyof CreateClientDto, string>>;
}

const EMPTY: CreateClientDto = {
    firstName: "",
    lastName: "",
    clientName: "",
    email: "",
    subscriptionModel: "PLAN",
    planId: "",
};

function cx(...parts: Array<string | false | null | undefined>) {
    return parts.filter(Boolean).join(" ");
}

export default function ClientFormModal({
    open,
    mode,
    initial,
    onClose,
    onSubmit,
    submitting = false,
    error,
    fieldErrors = {},
}: Props) {
    const isCreate = mode === "create";
    const isEdit = mode === "edit";

    // Wizard step only for create
    const [step, setStep] = useState<1 | 2>(1);
    const [form, setForm] = useState<CreateClientDto>(EMPTY);

    // Plans for PLAN model
    const [plans, setPlans] = useState<Plan[]>([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [plansError, setPlansError] = useState<string | null>(null);

    const subscriptionModel = (form as any).subscriptionModel as "PLAN" | "DEVICE" | undefined;
    const selectedPlanId = ((form as any).planId as string) || "";

    // ✅ compute without hooks (avoids hook-order issues)
    const canGoNext = (() => {
        if (!isCreate) return true;

        if (step === 1) {
            return (
                (form.firstName || "").trim().length > 0 &&
                (form.lastName || "").trim().length > 0 &&
                (form.clientName || "").trim().length > 0 &&
                (form.email || "").trim().length > 0
            );
        }

        if (step === 2) {
            if (subscriptionModel === "DEVICE") return true;
            if (subscriptionModel === "PLAN") return !!selectedPlanId;
            return false;
        }

        return true;
    })();

    useEffect(() => {
        if (!open) return;

        // reset on open
        setStep(1);
        setPlans([]);
        setPlansError(null);
        setPlansLoading(false);

        if (initial) {
            // edit: keep old behavior
            setForm({ ...EMPTY, ...(initial as any) });
        } else {
            setForm(EMPTY);
        }
    }, [open, initial]);

    // Fetch plans when create + PLAN chosen
    useEffect(() => {
        if (!open) return;
        if (!isCreate) return;
        if (subscriptionModel !== "PLAN") return;

        let cancelled = false;

        (async () => {
            try {
                setPlansLoading(true);
                setPlansError(null);

                // If you use /api prefix, change here:
                const res = await authApi.get<ApiResponse>("/plans");
                if (!res.data.success) throw new Error("Failed to load plans");

                const data = res.data.data;
                const list = (Array.isArray(data) ? data : [])
                    .filter((p: Plan) => p && p.isActive !== false)
                    .sort(
                        (a: Plan, b: Plan) =>
                            (a.allowedDevicesLimit ?? 0) - (b.allowedDevicesLimit ?? 0)
                    );

                if (cancelled) return;

                setPlans(list);

                // Auto-select first if none
                if (!((form as any).planId) && list[0]?._id) {
                    setForm((f) => ({ ...(f as any), planId: list[0]._id } as any));
                }
            } catch (e: any) {
                if (cancelled) return;
                setPlansError(e?.message || "Failed to load plans");
            } finally {
                if (!cancelled) setPlansLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isCreate, subscriptionModel]);

    if (!open) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isCreate && step === 1) {
            if (canGoNext) setStep(2);
            return;
        }

        const payload: any = { ...form };

        // Clean up payload
        if (isCreate) {
            if (subscriptionModel === "DEVICE") payload.planId = undefined;
            if (subscriptionModel === "PLAN" && !payload.planId) return;
        }

        await onSubmit(payload);
    };

    const StepPill = ({ n, label }: { n: 1 | 2; label: string }) => (
        <div className="flex items-center gap-2">
            <div
                className={cx(
                    "h-7 w-7 rounded-full border flex items-center justify-center text-xs font-semibold",
                    step === n ? "bg-black text-white border-black" : "bg-white text-gray-700"
                )}
            >
                {n}
            </div>
            <span
                className={cx(
                    "text-sm",
                    step === n ? "text-gray-900 font-semibold" : "text-gray-500"
                )}
            >
                {label}
            </span>
        </div>
    );

    const PlanCard = ({ p }: { p: Plan }) => {
        const selected = selectedPlanId === p._id;
        return (
            <button
                type="button"
                onClick={() => setForm((f) => ({ ...(f as any), planId: p._id } as any))}
                className={cx(
                    "w-full text-left rounded-2xl border p-4 transition hover:shadow-sm",
                    selected ? "border-black ring-2 ring-black/10" : "border-gray-200"
                )}
                disabled={submitting}
            >
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="inline-flex rounded-full border px-2 py-1 text-xs font-semibold">
                                {p.planKey}
                            </span>
                            <span className="text-sm font-semibold text-gray-900">{p.name}</span>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                            Allowed devices:{" "}
                            <span className="font-semibold text-gray-800">
                                {p.allowedDevicesLimit}
                            </span>
                        </p>
                        {p.description ? (
                            <p className="mt-2 text-xs text-gray-500 line-clamp-2">
                                {p.description}
                            </p>
                        ) : null}
                    </div>

                    {selected ? (
                        <CheckCircle2 className="text-black" size={18} />
                    ) : (
                        <div className="h-[18px] w-[18px] rounded-full border border-gray-300" />
                    )}
                </div>

                {typeof p.priceYearly === "number" && p?.priceYearly > 0 ? (
                    <div className="mt-3 text-xs text-gray-600">
                        Price:{" "}
                        <span className="font-semibold text-gray-900">{p.priceYearly}</span>{" "}
                        <span className="text-gray-500">
                            {(p.currency || "USD").toUpperCase()}
                        </span>{" "}
                        {p.billingCycle ? (
                            <span className="text-gray-400">/ {p.billingCycle}</span>
                        ) : null}
                    </div>
                ) : null}
            </button>
        );
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            aria-modal="true"
            role="dialog"
        >
            <form
                onSubmit={handleSubmit}
                className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl"
            >
                {/* Header */}
                <div className="mb-4 flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold">
                            {isEdit ? "Edit client" : "Create client"}
                        </h2>

                        {isCreate ? (
                            <div className="mt-3 flex items-center gap-6">
                                <StepPill n={1} label="Client info" />
                                <div className="h-px w-10 bg-gray-200" />
                                <StepPill n={2} label="Subscription" />
                            </div>
                        ) : null}
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl border px-3 py-1 text-sm hover:bg-gray-50"
                        disabled={submitting}
                    >
                        Close
                    </button>
                </div>

                {/* Top error */}
                {error ? (
                    <div className="mb-4 whitespace-pre-line rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        {error}
                    </div>
                ) : null}

                {/* Body */}
                <div className="mt-2">
                    {isEdit ? (
                        // EDIT MODE (unchanged)
                        <div className="grid grid-cols-1 gap-3">
                            <div>
                                <input
                                    className={cx(
                                        "w-full rounded-xl border px-3 py-2",
                                        fieldErrors.clientName && "border-red-400"
                                    )}
                                    placeholder="Client / Company name"
                                    value={form.clientName}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, clientName: e.target.value }))
                                    }
                                    required
                                    disabled={submitting}
                                />
                                {fieldErrors.clientName && (
                                    <p className="mt-1 text-xs text-red-600">
                                        {fieldErrors.clientName}
                                    </p>
                                )}
                            </div>

                            <div>
                                <input
                                    className={cx(
                                        "w-full rounded-xl border px-3 py-2",
                                        fieldErrors.email && "border-red-400"
                                    )}
                                    placeholder="Email"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, email: e.target.value }))
                                    }
                                    required
                                    disabled={submitting}
                                />
                                {fieldErrors.email && (
                                    <p className="mt-1 text-xs text-red-600">
                                        {fieldErrors.email}
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : step === 1 ? (
                        // CREATE step 1
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-600">
                                    First name
                                </label>
                                <input
                                    className={cx(
                                        "w-full rounded-xl border px-3 py-2",
                                        fieldErrors.firstName && "border-red-400"
                                    )}
                                    placeholder="First name"
                                    value={form.firstName}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, firstName: e.target.value }))
                                    }
                                    required
                                    disabled={submitting}
                                />
                                {fieldErrors.firstName && (
                                    <p className="mt-1 text-xs text-red-600">
                                        {fieldErrors.firstName}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-600">
                                    Last name
                                </label>
                                <input
                                    className={cx(
                                        "w-full rounded-xl border px-3 py-2",
                                        fieldErrors.lastName && "border-red-400"
                                    )}
                                    placeholder="Last name"
                                    value={form.lastName}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, lastName: e.target.value }))
                                    }
                                    required
                                    disabled={submitting}
                                />
                                {fieldErrors.lastName && (
                                    <p className="mt-1 text-xs text-red-600">
                                        {fieldErrors.lastName}
                                    </p>
                                )}
                            </div>

                            <div className="sm:col-span-2">
                                <label className="mb-1 block text-xs font-medium text-gray-600">
                                    Client / Company name
                                </label>
                                <input
                                    className={cx(
                                        "w-full rounded-xl border px-3 py-2",
                                        fieldErrors.clientName && "border-red-400"
                                    )}
                                    placeholder="Client / Company name"
                                    value={form.clientName}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, clientName: e.target.value }))
                                    }
                                    required
                                    disabled={submitting}
                                />
                                {fieldErrors.clientName && (
                                    <p className="mt-1 text-xs text-red-600">
                                        {fieldErrors.clientName}
                                    </p>
                                )}
                            </div>

                            <div className="sm:col-span-2">
                                <label className="mb-1 block text-xs font-medium text-gray-600">
                                    Email
                                </label>
                                <input
                                    className={cx(
                                        "w-full rounded-xl border px-3 py-2",
                                        fieldErrors.email && "border-red-400"
                                    )}
                                    placeholder="Email"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, email: e.target.value }))
                                    }
                                    required
                                    disabled={submitting}
                                />
                                {fieldErrors.email && (
                                    <p className="mt-1 text-xs text-red-600">
                                        {fieldErrors.email}
                                    </p>
                                )}
                            </div>

                            <div className="sm:col-span-2 mt-2 rounded-2xl border bg-gray-50 p-4">
                                <p className="text-sm font-semibold text-gray-900">
                                    Next: choose subscription
                                </p>
                                <p className="mt-1 text-xs text-gray-600">
                                    You’ll pick whether this client is on <b>Plan-based</b>{" "}
                                    subscription or <b>Device licensing</b>.
                                </p>
                            </div>
                        </div>
                    ) : (
                        // CREATE step 2
                        <div className="space-y-4">
                            <div className="rounded-2xl border p-4">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900">
                                            Subscription model
                                        </p>
                                        <p className="mt-1 text-xs text-gray-600">
                                            Plan = device limit by plan. Device = license per device.
                                        </p>
                                    </div>

                                    <div className="inline-flex rounded-xl border bg-white p-1">
                                        <button
                                            type="button"
                                            className={cx(
                                                "rounded-lg px-3 py-2 text-sm",
                                                subscriptionModel !== "DEVICE"
                                                    ? "bg-black text-white"
                                                    : "text-gray-700"
                                            )}
                                            onClick={() =>
                                                setForm((f) =>
                                                    ({ ...(f as any), subscriptionModel: "PLAN" } as any)
                                                )
                                            }
                                            disabled={submitting}
                                        >
                                            Plan
                                        </button>

                                        <button
                                            type="button"
                                            className={cx(
                                                "rounded-lg px-3 py-2 text-sm",
                                                subscriptionModel === "DEVICE"
                                                    ? "bg-black text-white"
                                                    : "text-gray-700"
                                            )}
                                            onClick={() =>
                                                setForm((f) =>
                                                ({
                                                    ...(f as any),
                                                    subscriptionModel: "DEVICE",
                                                    planId: "",
                                                } as any)
                                                )
                                            }
                                            disabled={submitting}
                                        >
                                            Device
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {subscriptionModel === "PLAN" ? (
                                <div className="rounded-2xl border p-4">
                                    <div className="mb-3 flex items-start justify-between gap-2">
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900">
                                                Choose a plan
                                            </p>
                                            <p className="mt-1 text-xs text-gray-600">
                                                This selection is used for plan renewals and device limit
                                                enforcement.
                                            </p>
                                        </div>
                                        {plansLoading ? (
                                            <span className="text-xs text-gray-500">
                                                Loading plans…
                                            </span>
                                        ) : null}
                                    </div>

                                    {plansError ? (
                                        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                                            {plansError}
                                        </div>
                                    ) : null}

                                    {!plansLoading && plans.length === 0 ? (
                                        <div className="rounded-xl border border-dashed p-4 text-sm text-gray-500">
                                            No plans available.
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            {plans.map((p) => (
                                                <PlanCard key={p._id} p={p} />
                                            ))}
                                        </div>
                                    )}

                                    {!selectedPlanId ? (
                                        <p className="mt-3 text-xs text-red-600">
                                            Please select a plan to continue.
                                        </p>
                                    ) : null}
                                </div>
                            ) : (
                                <div className="rounded-2xl border p-4">
                                    <p className="text-sm font-semibold text-gray-900">
                                        Device licensing
                                    </p>
                                    <p className="mt-1 text-xs text-gray-600">
                                        This client will be billed per device. Invoices will be
                                        attached during device creation / renewals.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="mt-6 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        {isCreate && step === 2 ? (
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 hover:bg-gray-50"
                                disabled={submitting}
                            >
                                <ChevronLeft size={16} />
                                Back
                            </button>
                        ) : null}
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-xl border px-4 py-2 hover:bg-gray-50"
                            disabled={submitting}
                        >
                            Cancel
                        </button>

                        {isCreate ? (
                            step === 1 ? (
                                <button
                                    type="submit"
                                    className="inline-flex items-center gap-2 rounded-xl bg-black px-4 py-2 text-white hover:opacity-90 disabled:opacity-60"
                                    disabled={submitting || !canGoNext}
                                    title={!canGoNext ? "Please fill all required fields" : undefined}
                                >
                                    Next <ChevronRight size={16} />
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    className="rounded-xl bg-black px-4 py-2 text-white hover:opacity-90 disabled:opacity-60"
                                    disabled={submitting || !canGoNext}
                                >
                                    {submitting ? "Creating…" : "Create"}
                                </button>
                            )
                        ) : (
                            <button
                                type="submit"
                                className="rounded-xl bg-black px-4 py-2 text-white hover:opacity-90 disabled:opacity-60"
                                disabled={submitting}
                            >
                                {submitting ? "Saving…" : "Save changes"}
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
}
